/**
 * Каталог шаблонов партнёра — один на весь раздел.
 *
 * Раньше он жил внутри `Templates.tsx`, и вкладка «Уровни» показывала свой
 * собственный список: нативный `<select>` на три десятка пунктов, без
 * поиска, цен и тегов. Владелец справедливо назвал его неудобным — но
 * дописать поиск во второй список значило бы завести вторую копию правил
 * «что показывать» и «что можно выбрать». Однажды они разошлись бы, и
 * в панели выбора оказалось бы не то, что на вкладке каталога.
 *
 * Поэтому компонент один и умеет два режима:
 *  - `onToggle` не передан — просто каталог (вкладка «Шаблоны»);
 *  - передан — с галочками, для набора уровня.
 *
 * Шаблоны `needPrompt` показаны серыми и в обоих режимах невыбираемы:
 * поля под текстовый промпт в контенте нет. Спрятать их нельзя — владелец
 * найдёт их в панели партнёра и будет искать, почему их нет у нас.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { request } from '../../api/client.js'
import type { TemplateRow, TemplatesResponse } from '../../api/types.js'
import { DataTable, type Column } from '../../components/DataTable.js'
import { Badge, Notice } from '../../components/ui.js'

type DurationFilter = '' | '7' | '10'

const PREVIEW_WIDTH = 112
const PREVIEW_HEIGHT = 63

/**
 * Превью шаблона: кадр, а при наведении — беззвучный пример ролика.
 *
 * Ради этого блока задача и делалась. Владелец: «не видишь, что в этих
 * шаблонах» — и был прав: `tpl_c99766a4139440338dfd8c8987b741ee` не говорит
 * ничего, а выбирать по нему приходилось из трёх десятков.
 *
 * Видео грузится ТОЛЬКО по наведению. Тридцать автоплеев в списке — это
 * тридцать одновременных загрузок с чужого CDN на каждое открытие панели.
 *
 * Ссылки публичные и партнёрские, показываются только здесь. Наши ролики
 * так наружу не уходят никогда — у них подписанные ссылки на 15 минут
 * (правило №5).
 */
function Preview({ row }: { row: TemplateRow }): JSX.Element {
  const [playing, setPlaying] = useState(false)

  const box: React.CSSProperties = {
    width: PREVIEW_WIDTH,
    height: PREVIEW_HEIGHT,
    borderRadius: 'var(--radius-sm)',
    objectFit: 'cover',
    background: 'var(--surface-2, #1b1b1f)',
    display: 'block',
  }

  if (row.posterUrl === null) {
    return (
      <div
        style={{ ...box, display: 'grid', placeItems: 'center', border: '1px solid var(--border)' }}
        className="muted"
      >
        {/* Явная подпись, а не пустое место: пустое читается как
            «картинка не загрузилась», и владелец лезет чинить сеть. */}
        <span style={{ fontSize: 10 }}>без превью</span>
      </div>
    )
  }

  return (
    <div
      onMouseEnter={() => setPlaying(row.videoUrl !== null)}
      onMouseLeave={() => setPlaying(false)}
      style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
    >
      {playing && row.videoUrl !== null ? (
        <video src={row.videoUrl} style={box} autoPlay muted loop playsInline poster={row.posterUrl} />
      ) : (
        <img src={row.posterUrl} alt="" style={box} loading="lazy" />
      )}
    </div>
  )
}

export interface TemplateCatalogProps {
  /** Передан — режим выбора: у строк появляются галочки. */
  onToggle?: (id: string) => void
  /** Что уже в наборе, в порядке показа. Номер рисуется на галочке. */
  selected?: readonly string[]
  /** Каталог целиком — чтобы владелец панели знал цены для итога. */
  onLoaded?: (templates: TemplateRow[]) => void
  /** Ограничить высоту списка: в панели он живёт рядом с набором. */
  maxHeight?: number
}

export function TemplateCatalog({
  onToggle,
  selected = [],
  onLoaded,
  maxHeight,
}: TemplateCatalogProps): JSX.Element {
  const [q, setQ] = useState('')
  const [appliedQ, setAppliedQ] = useState('')
  const [duration, setDuration] = useState<DurationFilter>('')
  const [rows, setRows] = useState<TemplateRow[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Счётчик запросов, а не флаг отмены: `load` зовут и эффект (смена
   * поиска/длительности), и кнопка «обновить каталог». Без счётчика
   * устаревший ответ мог бы примениться ПОСЛЕ свежего.
   */
  const requestIdRef = useRef(0)

  const load = useCallback(
    async (opts?: { refresh?: boolean }): Promise<void> => {
      const myId = ++requestIdRef.current
      setLoading(true)
      try {
        const data = await request<TemplatesResponse>('/admin/musebox/templates', {
          query: {
            q: appliedQ === '' ? undefined : appliedQ,
            duration: duration === '' ? undefined : duration,
            refresh: opts?.refresh === true ? 1 : undefined,
          },
        })
        if (requestIdRef.current !== myId) return
        setRows(data.templates)
        setWarnings(data.warnings)
        setError(null)
        onLoaded?.(data.templates)
      } catch (err) {
        if (requestIdRef.current !== myId) return
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (requestIdRef.current === myId) setLoading(false)
      }
    },
    [appliedQ, duration, onLoaded],
  )

  useEffect(() => {
    void load()
  }, [load])

  const picking = onToggle !== undefined

  const columns: Column<TemplateRow>[] = [
    ...(picking
      ? [
          {
            key: 'pick',
            title: '',
            render: (row: TemplateRow) => {
              const at = selected.indexOf(row.id)
              return (
                <button
                  className={at === -1 ? 'btn btn--ghost btn--sm' : 'btn btn--sm'}
                  type="button"
                  disabled={row.needPrompt}
                  onClick={() => onToggle(row.id)}
                  // Номер на кнопке — это место в наборе, а не «сколько раз
                  // выбран»: второй раз тот же шаблон в набор не попадает.
                  title={at === -1 ? 'добавить в набор' : `в наборе, ${String(at + 1)}-й`}
                >
                  {at === -1 ? '+' : `✓ ${String(at + 1)}`}
                </button>
              )
            },
          },
        ]
      : []),
    {
      key: 'preview',
      title: '',
      render: (row) => <Preview row={row} />,
    },
    {
      key: 'name',
      title: 'Шаблон',
      render: (row) => (
        <div className={row.needPrompt ? 'muted' : undefined}>
          <div>{row.name}</div>
          <div className="mono" style={{ fontSize: 11 }}>
            {row.id}
          </div>
          {row.needPrompt && <div style={{ fontSize: 11 }}>нужен промпт — пока не поддерживается</div>}
        </div>
      ),
    },
    {
      key: 'price',
      title: 'Цена',
      numeric: true,
      render: (row) => (row.price === null ? '—' : row.price),
    },
    /**
     * Теги, длительности и «где используется» — только на вкладке каталога.
     * В панели набор стоит рядом со списком, и эти четыре колонки раздували
     * таблицу настолько, что набор сваливался под неё. Справочные сведения
     * остаются в одном клике на вкладке «Шаблоны», а рядом с выбором нужны
     * название и цена.
     */
    ...(picking
      ? []
      : [
          {
            key: 'tags',
            title: 'Теги',
            render: (row: TemplateRow) => (row.tags.length === 0 ? '—' : row.tags.join(', ')),
          },
          {
            key: 'd7',
            title: '7 с',
            render: (row: TemplateRow) => (row.duration7sEnabled ? '✓' : '—'),
          },
          {
            key: 'd10',
            title: '10 с',
            render: (row: TemplateRow) => (row.duration10sEnabled ? '✓' : '—'),
          },
          {
            key: 'usedBy',
            title: 'Где используется',
            render: (row: TemplateRow) =>
              row.usedBy.length === 0 ? '—' : <span className="mono">{row.usedBy.join(', ')}</span>,
          },
        ]),
  ]

  return (
    <div className="stack stack--sm">
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          setAppliedQ(q.trim())
        }}
      >
        <input
          className="input"
          style={{ width: 220 }}
          placeholder="поиск по id или названию"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="select"
          style={{ width: 200 }}
          value={duration}
          onChange={(e) => setDuration(e.target.value as DurationFilter)}
        >
          <option value="">любая длительность</option>
          <option value="7">умеет 7 с</option>
          <option value="10">умеет 10 с</option>
        </select>
        <button className="btn btn--sm" type="submit">
          Искать
        </button>
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          disabled={loading}
          onClick={() => void load({ refresh: true })}
        >
          {loading ? 'Обновляю…' : 'Обновить каталог'}
        </button>
        {picking && selected.length > 0 && <Badge tone="ok">в наборе: {selected.length}</Badge>}
      </form>

      {warnings.map((w) => (
        <Notice key={w} tone="warn">
          {w}
        </Notice>
      ))}

      <div style={maxHeight === undefined ? undefined : { maxHeight, overflowY: 'auto' }}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={loading}
          error={error}
          empty={warnings.length > 0 ? 'Каталог пуст.' : 'Интеграция с партнёром выключена, или каталог пока пуст.'}
        />
      </div>
    </div>
  )
}
